import { useCallback, useEffect } from 'react';
import { baseUrl, postFormDataRequest } from '../utils/services';
import { useErrorStore } from '../stores/useErrorStore';
import { useTicketStore } from '../stores/useTicketStore';
import { useUploadStore } from '../stores/useUploadStore';

export function useChatUploads() {
  const currentChat = useTicketStore((state) => state.currentChat);

  const selectedImageToSend = useUploadStore((state) => state.selectedImageToSend);
  const selectedVideoToSend = useUploadStore((state) => state.selectedVideoToSend);
  const selectedDocumentToSend = useUploadStore((state) => state.selectedDocumentToSend);
  const setUrlImageToSend = useUploadStore((state) => state.setUrlImageToSend);
  const setUrlVideoToSend = useUploadStore((state) => state.setUrlVideoToSend);
  const setUrlDocumentToSend = useUploadStore((state) => state.setUrlDocumentToSend);
  const setIsMediaUploadLoading = useUploadStore((state) => state.setIsMediaUploadLoading);
  const setMediaUploadProgress = useUploadStore((state) => state.setMediaUploadProgress);

  const setErrorMessage = useErrorStore((state) => state.setErrorMessage);

  const handleFileUpload = useCallback(async (
    files,
    uploadType,
    urlSetter,
  ) => {
    if (files.length === 0) {
      setErrorMessage(`Nenhum ${uploadType} foi selecionado para upload`);
      return;
    }

    const validateFile = (file) => {
      const validTypes = {
        video: 'video/',
        image: 'image/',
        audio: 'audio/',
        document: '',
      };

      if (uploadType === 'document') {
        return true;
      }

      if (!file.type.startsWith(validTypes[uploadType])) {
        setErrorMessage('Formato de arquivo invÃ¡lido!');
        return false;
      }

      return true;
    };

    const uploadFile = async (file) => {
      const formData = new FormData();
      formData.append(`${uploadType}s`, file);

      const config = {
        onUploadProgress: (percentCompleted) => {
          setMediaUploadProgress(percentCompleted);
        },
      };

      if (currentChat?.is_internal_chat === 1) {
        return postFormDataRequest(
          `${baseUrl}/messages/generate-presigned-${uploadType}-urls`,
          formData,
        );
      }

      return postFormDataRequest(
        `${baseUrl}/messages/upload-${uploadType}s`,
        formData,
        config,
      );
    };

    try {
      setIsMediaUploadLoading(true);
      setMediaUploadProgress(0);

      const responseData = await Promise.all(
        Array.from(files)
          .filter(validateFile)
          .map(uploadFile),
      );

      urlSetter(responseData.flatMap((data) => data));
    } catch (error) {
      setErrorMessage(
        `NÃ£o foi possÃ­vel fazer upload do ${uploadType}! Entre em contato com o suporte.`,
      );
    } finally {
      setIsMediaUploadLoading(false);
      setMediaUploadProgress(100);
    }
  }, [
    currentChat?.is_internal_chat,
    setErrorMessage,
    setIsMediaUploadLoading,
    setMediaUploadProgress,
  ]);

  const imageUpload = useCallback(async () => {
    await handleFileUpload(selectedImageToSend, 'image', setUrlImageToSend);
  }, [handleFileUpload, selectedImageToSend, setUrlImageToSend]);

  const videoUpload = useCallback(async () => {
    await handleFileUpload(selectedVideoToSend, 'video', setUrlVideoToSend);
  }, [handleFileUpload, selectedVideoToSend, setUrlVideoToSend]);

  const documentUpload = useCallback(async () => {
    const disallowedExtensions = [
      'php',
      'js',
      'py',
      'rb',
      'java',
      'cpp',
      'c',
      'cs',
      'swift',
      'go',
      'kt',
      'ts',
      'sql',
      'sh',
      'pl',
      'lua',
      'r',
      'dart',
      'bash',
      'yaml',
      'ini',
      'toml',
      'exe',
    ];

    const hasInvalidFile = selectedDocumentToSend.some((file) => {
      const fileExtension = file.name.split('.').pop().toLowerCase();
      return disallowedExtensions.includes(fileExtension);
    });

    if (hasInvalidFile) {
      setErrorMessage('Tipo de arquivo invÃ¡lido!');
      return;
    }

    await handleFileUpload(selectedDocumentToSend, 'document', setUrlDocumentToSend);
  }, [
    handleFileUpload,
    selectedDocumentToSend,
    setErrorMessage,
    setUrlDocumentToSend,
  ]);

  useEffect(() => {
    if (selectedImageToSend?.length > 0) {
      imageUpload();
    }

    if (selectedVideoToSend?.length > 0) {
      videoUpload();
    }

    if (selectedDocumentToSend?.length > 0 && selectedDocumentToSend[0].name) {
      documentUpload();
    }
  }, [
    documentUpload,
    imageUpload,
    selectedDocumentToSend,
    selectedImageToSend,
    selectedVideoToSend,
    videoUpload,
  ]);
}
